import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

/**
 * Breadcrumbs, the desktop replacement for the mobile floating back button
 * (DESKTOP-PREFERENCES section 5). Present on every MarketShell page and in the
 * ConsoleShell utility bar.
 *
 * Markup follows the `.dsk-breadcrumbs` contract in desktop/system.css exactly:
 * a flex <nav> whose DIRECT children are the links, the `.sep` separators and
 * the `.is-current` leaf. Wrapping them in an <ol> would put a list between the
 * flex container and its intended children and the gap rule would stop
 * applying, so the landmark plus `aria-current` carries the semantics instead.
 *
 * Desktop only. Below 1024 a page takes a back button, not a trail.
 *
 * A crumb may carry a router `state` alongside its `to`. That is how a page
 * deep in a subtree hands back what its parent needs to know and cannot work
 * out for itself -- the results page a card was opened from, say. Without it a
 * trail is a one-way street: every step up forgets what the step down knew.
 *
 * @param {{
 *   items: Array<{ label: string, to?: string, state?: any }>,
 *   label?: string,
 *   className?: string
 * }} props
 */
export function Breadcrumbs({ items = [], label = 'Breadcrumb', className }) {
  if (items.length === 0) return null;

  return (
    <nav className={cn('dsk-breadcrumbs', className)} aria-label={label}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const key = `${item.label}-${index}`;

        return (
          <React.Fragment key={key}>
            {index > 0 && (
              <span className="sep" aria-hidden="true">
                /
              </span>
            )}
            {isLast || !item.to ? (
              <span className="is-current" aria-current={isLast ? 'page' : undefined}>
                {item.label}
              </span>
            ) : (
              <Link to={item.to} state={item.state}>
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default Breadcrumbs;
